const DB_NAME = "phonebook_durable_uploads";
const DB_VERSION = 1;
const STORE = "uploads";

const requestToPromise = (request) =>
    new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error || new Error("IndexedDB request failed"));
    });

const openDb = () =>
    new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") {
            reject(new Error("IndexedDB unavailable"));
            return;
        }
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: "id" });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error || new Error("Failed to open upload DB"));
    });

export const savePersistedUpload = async (record) => {
    if (!record?.id || !record?.blob) return null;
    const db = await openDb();
    try {
        const tx = db.transaction(STORE, "readwrite");
        const store = tx.objectStore(STORE);
        await requestToPromise(
            store.put({
                id: String(record.id),
                kind: record.kind,
                targetId: String(record.targetId),
                fileName: record.fileName || "file",
                mime: record.mime || "",
                shareType: record.shareType || "",
                fileType: record.fileType || "document",
                fileSize: record.fileSize || record.blob.size || 0,
                progress: record.progress ?? 0,
                status: record.status || "pending",
                createdAt: record.createdAt || Date.now(),
                blob: record.blob,
            })
        );
        await new Promise((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
        return record.id;
    } finally {
        db.close();
    }
};

export const updatePersistedUpload = async (id, patch = {}) => {
    if (!id) return null;
    const key = String(id);
    const db = await openDb();
    try {
        const tx = db.transaction(STORE, "readwrite");
        const store = tx.objectStore(STORE);
        const existing = await requestToPromise(store.get(key));
        if (!existing) return null;
        await requestToPromise(store.put({ ...existing, ...patch, id: key }));
        await new Promise((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
        return key;
    } finally {
        db.close();
    }
};

export const removePersistedUpload = async (id) => {
    if (!id) return;
    const db = await openDb();
    try {
        const tx = db.transaction(STORE, "readwrite");
        await requestToPromise(tx.objectStore(STORE).delete(String(id)));
        await new Promise((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    } finally {
        db.close();
    }
};

export const listPersistedUploads = async () => {
    try {
        const db = await openDb();
        try {
            const tx = db.transaction(STORE, "readonly");
            const rows = await requestToPromise(tx.objectStore(STORE).getAll());
            return rows || [];
        } finally {
            db.close();
        }
    } catch {
        return [];
    }
};

export const clearPersistedUploads = async () => {
    try {
        const db = await openDb();
        try {
            const tx = db.transaction(STORE, "readwrite");
            await requestToPromise(tx.objectStore(STORE).clear());
            await new Promise((resolve, reject) => {
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
                tx.onabort = () => reject(tx.error);
            });
        } finally {
            db.close();
        }
    } catch {
        /* ignore */
    }
};

export const blobToFile = (blob, fileName, mime) => {
    if (blob instanceof File) return blob;
    return new File([blob], fileName || "file", {
        type: mime || blob?.type || "application/octet-stream",
        lastModified: Date.now(),
    });
};
